<script setup lang="ts">
/** Aura Incorporation — DDF: General Notice of Disclosure of Interest (Companies Act 1967, s.156 & s.165). */
import { inject, onMounted, ref, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

/**
 * 이해관계 구분 체크 상태.
 *
 * ⚠ 원본 DDF 는 값들이 전부 `[[if own_name_interests]] … [[/if]]` 같은 **조건 블록 안**에 있다.
 *   이 플래그가 payload 에 실리지 않으면 조건이 거짓으로 평가돼 **표가 통째로 삭제**되고,
 *   사용자가 입력한 Registered Holder·금액 등도 함께 사라진다.
 *   → 화면 로컬 상태로만 두지 말고 반드시 fill(문서 값)에 기록한다.
 */
const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const FLAG_KEYS = [
  'other_interests',
  'own_name_interests',
  'family_interests',
  'deemed_interests',
  'corporate_interests',
] as const;

const DEFAULTS: Record<string, boolean> = {
  other_interests: true,
  own_name_interests: true,
  family_interests: false,
  deemed_interests: false,
  corporate_interests: false,
};

const flags = ref<Record<string, boolean>>({ ...DEFAULTS });

function readFlag(k: string): boolean {
  const v = String(fill.values[K(k)] ?? '').trim().toLowerCase();
  if (!v) return DEFAULTS[k];
  return v === 'yes' || v === 'true' || v === '1';
}
function syncFromFill() {
  for (const k of FLAG_KEYS) flags.value[k] = readFlag(k);
}
function toggle(k: string) {
  flags.value[k] = !flags.value[k];
  fill.setValue(K(k), flags.value[k] ? 'yes' : 'no');
}

onMounted(() => {
  // 값이 없으면 기본값을 payload 에 심어 둔다(미설정 = 조건 거짓 = 블록 삭제 방지)
  for (const k of FLAG_KEYS) {
    if (!String(fill.values[K(k)] ?? '').trim()) {
      fill.setValue(K(k), DEFAULTS[k] ? 'yes' : 'no');
    }
  }
  syncFromFill();
});

// 편집 복원(restoreState) 등 외부에서 값이 바뀌면 체크 표시도 따라간다
watch(
  () => FLAG_KEYS.map((k) => fill.values[K(k)]).join('|'),
  () => syncFromFill(),
);

const INTEREST_SECTIONS = [
  { key: 'own_name_interests', label: 'Interest registered in own name' },
  { key: 'family_interests', label: 'Interest of immediate family members' },
  { key: 'deemed_interests', label: 'Other deemed interest' },
  { key: 'corporate_interests', label: 'In name of Bodies Corporate' },
];
</script>

<template>
  <div class="allot-doc">
    <p class="doc-p">To: The Board of Directors<br /><CddVarField field-key="company_name" label="Company Name" /> ("The Company")</p>
    <div class="doc-lbl-title">GENERAL NOTICE OF DISCLOSURE OF INTEREST</div>
    <p class="doc-p">
      In accordance with Section 156 of the Singapore Companies Act 1967, I hereby give general notice that I am also a director or
      member or Chief Executive Officer of the corporation or firms set out below and I am to be regarded as interested in any
      transaction/s which may, after the date of this notice, be made with such corporations or firms. I will notify the company of
      any changes in respect of this notification as soon as practicable.
    </p>
    <p class="doc-p">
      <span class="doc-toggle" :class="{ off: !flags.other_interests }" @click="toggle('other_interests')">{{ flags.other_interests ? '☑' : '☐' }} Corporations / firms</span>
    </p>
    <table v-if="flags.other_interests" class="cdd-tbl">
      <thead><tr><th>Name of Corporation<sup>1</sup> / Firm<sup>2</sup></th><th>Nature of Interest(s)<sup>3</sup> (Director / Member / Partner / Officer)</th></tr></thead>
      <tbody><tr><td><CddVarField field-key="corporation_name" label="Corporation Name" /></td><td><CddVarField field-key="interest_nature" label="Interest Nature" /></td></tr></tbody>
    </table>

    <div class="doc-lbl-title" style="margin-top:14px">NOTICE OF DIRECTORS' / CHIEF EXECUTIVE OFFICER'S INTEREST IN THE COMPANY AND ITS RELATED CORPORATIONS</div>
    <p class="doc-p">
      In accordance with Section 165 of the Singapore Companies Act 1967, I hereby notify you of the changes in particulars of my
      interests in the shares, debentures, participatory interests, rights, options and contracts relating to shares of the company
      and/or its related corporation/s on the date mentioned below, being the day on which I was appointed a Director or Chief
      Executive Officer of the Company. In the event of any changes, I shall notify the Company in writing within 2 days.
    </p>

    <template v-for="s in INTEREST_SECTIONS" :key="s.key">
      <p class="doc-p">
        <span class="doc-toggle" :class="{ off: !flags[s.key] }" @click="toggle(s.key)">{{ flags[s.key] ? '☑' : '☐' }} {{ s.label }}</span>
      </p>
      <table v-if="flags[s.key]" class="cdd-tbl">
        <thead><tr><th>Name of Registered Holder</th><th>Description of Interests</th><th>Amount at Date of Appointment / Balance at Beginning</th><th>Balance at End of Financial Year / Period</th><th>Date Acquired / Disposed &amp; Price</th></tr></thead>
        <tbody><tr>
          <td><CddVarField field-key="registered_holder" label="Registered Holder" /></td>
          <td><CddVarField field-key="interest_description" label="Interest Description" /></td>
          <td><CddVarField field-key="amount_at_appointment" label="Amount at Appointment" /></td>
          <td><CddVarField field-key="amount_at_end" label="Amount at End" /></td>
          <td><CddVarField field-key="date_price_details" label="Date / Price Details" /></td>
        </tr></tbody>
      </table>
    </template>

    <p class="doc-note" style="margin-top:8px">
      1. Interest registered in the directors' name, the beneficial interest of which is vested in another party should be disclosed together with details of the beneficial shareholders.
      2. A director's / CEO's interests includes beneficial interests of his immediate family (spouse and children incl. step-/adopted children, none of whom are directors).
      3. Section 7 of the Companies Act 1967 defines other deemed interests (e.g. interests under trust, and through associated persons or corporations).
      4. Bodies Corporate refers to companies in which a director has a controlling interest or, with associates, controls more than 20% of voting shares.
      N.B. Please indicate 'NIL' if no interest to be disclosed.
    </p>
    <p class="doc-p" style="margin-top:10px"><b>Name of Director / Chief Executive Officer:</b> <CddVarField field-key="director_name" label="Director Name" /></p>
    <p class="doc-p"><b>Date:</b> <CddVarField field-key="notice_date" label="Notice Date" /></p>
  </div>
</template>

<style scoped>
.doc-note { font-size: 9.5px; color: #555; line-height: 1.5; }
.doc-toggle.off { opacity: .5; }
</style>
